import {
  SelectionControlsGroupSize,
  SelectionControlsGroupSizes,
  SelectionControlsGroupWeight,
} from 'omakase/SelectionControls/SelectionControlsGroupCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { generateResponsiveMapping } from 'omakase/utils/responsiveSizeMap';

export const baseElement = () => css`
  width: 100%;
`;

const visuallyHiddenLegendStyles = css`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

// The radio items/checkboxes themselves have a top-padding of 1.5 spacing units,
// another 2.25 under the header is needed to get them to match the mocks.
const plainHeader = ({ spacing }: Theme) => css`
  margin-bottom: ${spacing(2.25)}px;
`;

export const header = (
  theme: Theme,
  { hideLabel, plain }: { hideLabel?: boolean; plain?: boolean }
) => [hideLabel && visuallyHiddenLegendStyles, plain && plainHeader(theme)];

export const accordionHeader = (theme: Theme, closed: boolean) => [
  css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  !closed && plainHeader(theme),
];

export const invisibleChoices = css`
  display: none;
`;

export const getTypographyFromSizeAndWeight = (
  { typography: { variant } }: Theme,
  size: SelectionControlsGroupSizes,
  weight: SelectionControlsGroupWeight
) => {
  switch (size) {
    case 'small':
      switch (weight) {
        case 'book':
          return variant('caption');
        case 'medium':
          return variant('captionStrong');
        case 'bold':
          return variant('captionStrong');
        default:
          assertNever(weight);
      }
      break;
    case 'large':
      switch (weight) {
        case 'book':
          return variant('body02');
        case 'medium':
          return variant('body02Strong');
        case 'bold':
          return variant('body02Strong');
        default:
          assertNever(weight);
      }
      break;
    case 'jumbo':
      switch (weight) {
        case 'book':
          return variant('body01');
        case 'medium':
          return variant('body01Strong');
        case 'bold':
          return variant('body01Strong');
        default:
          assertNever(weight);
      }
      break;
    default:
      assertNever(size);
  }
};

export const labelText = (
  theme: Theme,
  size: SelectionControlsGroupSize,
  weight: SelectionControlsGroupWeight
) => {
  const styles = generateResponsiveMapping<SelectionControlsGroupSizes>(theme, size, (respSize) =>
    getTypographyFromSizeAndWeight(theme, respSize, weight)
  );

  return [
    [...styles],
    css`
      color: ${theme.colors.fgDefault};
    `,
  ];
};

export const helperText = (
  { colors, spacing, typography }: Theme,
  labelSize: SelectionControlsGroupSize
) => [
  labelSize === 'jumbo' ? typography.variant('body02') : typography.variant('caption'),
  css`
    color: ${colors.fgSubtle};
    margin-top: ${spacing(0.5)}px;
    &.Mui-disabled {
      color: ${colors.fgDisabled};
    }
  `,
];

export const closeOrShowLink = ({ spacing }: Theme) => css`
  align-self: flex-start;
  margin-top: ${spacing(2.5)}px;
`;

const getGridTemplateColumnsString = (numColumns: number) => {
  return Array.from(Array(numColumns)).reduce<string>(
    (prev, _current) => (prev += 'minmax(0, 1fr) '),
    ''
  );
};

// Ensures consistent grid row height in Safari
// https://stackoverflow.com/questions/55347359/why-is-css-grid-row-height-different-in-safari
export const formGroup = css`
  display: grid;
`;

export const staticGrid = ({ breakpoints, spacing }: Theme, smColumns: number) => css`
  ${breakpoints.up('sm')} {
    display: grid;
    grid-template-columns: ${getGridTemplateColumnsString(smColumns)};
    column-gap: ${spacing(2)}px;
  }
`;
